
import { supabase } from '../../client';
import { CustomerStatus } from '@/lib/supabase/customers/types/CustomerTypes';
import { Customer } from '../types';
import { addShopInfoToCustomers } from './customerShops';

/**
 * Get customers with a specific status filter
 */
export const getCustomersWithStatusFilter = async (
  organizationId: string,
  status: CustomerStatus,
  shopId?: string
): Promise<Customer[]> => {
  console.log(`[getCustomersWithStatusFilter] Retrieving customers with status: ${status}`, shopId ? `and shop: ${shopId}` : '');
  
  if (!organizationId) {
    console.error('[getCustomersWithStatusFilter] No organization ID provided, cannot fetch customers');
    return [];
  }
  
  try {
    // Base query for all customers in the organization with a count of their orders
    let query = supabase
      .from('customers')
      .select('*, orders(id, payment_status)')
      .eq('organization_id', organizationId);
    
    // Add shop filter if provided
    if (shopId) {
      query = query.eq('shop_id', shopId);
    }
    
    // Execute query
    const { data: customers, error } = await query;
    
    if (error) {
      console.error('[getCustomersWithStatusFilter] Error retrieving customers:', error);
      return [];
    }
    
    if (!customers || customers.length === 0) {
      console.log('[getCustomersWithStatusFilter] No customers found with specified filters');
      return [];
    }
    
    console.log(`[getCustomersWithStatusFilter] Retrieved ${customers.length} customers from database with organization ID: ${organizationId}`);
    
    // Add shop information to customers
    const customersWithShops = await addShopInfoToCustomers(customers || []);
    
    // Transform customers to add orders_count property and handle payment status
    const processedCustomers = customersWithShops.map(customer => {
      // For PostgreSQL foreign table relationships, orders will be an array
      const orders = (customer as any).orders || [];
      const orderCount = customer.orders_count || orders.length || 0;
      
      // Check for paid and unpaid orders
      const paidOrders = orders.filter(order => order.payment_status === 'paid').length;
      const unpaidOrders = orders.filter(order => 
        ['unpaid', 'partial'].includes(order.payment_status)
      ).length;
      
      return {
        ...customer,
        orders_count: orderCount,
        paid_orders_count: paidOrders,
        unpaid_orders_count: unpaidOrders,
        has_paid_orders: paidOrders > 0,
        has_unpaid_orders: unpaidOrders > 0
      };
    });
    
    console.log(`[getCustomersWithStatusFilter] Processed ${processedCustomers.length} customers with shop info`);
    
    // Process customers based on status
    if (status === 'all') {
      return processedCustomers;
    } 
    else if (status === 'pending') {
      // Pending customers are new (created in the last 7 days)
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      
      return processedCustomers.filter(customer => {
        const customerDate = new Date(customer.created_at);
        return customerDate > oneWeekAgo;
      });
    }
    else if (status === 'paid') {
      // Clients réguliers avec des commandes payées
      return processedCustomers.filter(customer => 
        customer.has_paid_orders
      );
    }
    else if (status === 'unpaid') {
      // Clients avec des commandes non payées
      return processedCustomers.filter(customer => 
        customer.has_unpaid_orders
      );
    }
    
    return processedCustomers;
  } catch (error) {
    console.error('[getCustomersWithStatusFilter] Error:', error);
    return [];
  }
};
